/**
 * `<PendingAttachmentStrip>` — the horizontal chip list shown while
 * OS-file-dropped attachments upload (and, on the comment composer, until
 * the comment is posted). Controlled + presentational: the caller owns
 * the item list + lifecycle and passes `onRemove`; this component only
 * renders and localizes.
 *
 * Chip anatomy is lifted from the chat composer's pending strip
 * (`ChatComposer.tsx`) so the two surfaces read identically — 40px leading
 * thumb (image object-URL, else a kind glyph), truncating filename, a
 * status subline (uploading / size / error), a progress bar while
 * in-flight, and an absolute cancel/remove button.
 *
 * The bar is determinate once the caller supplies `progress` and
 * indeterminate until then — presign has no byte count, and a chip sitting
 * at a hard 0% reads as stalled rather than as starting.
 *
 * Copy comes from the shared `uploads` namespace so card + comment
 * surfaces stay in sync.
 */

import { formatBytes } from '@/features/chat/lib/format-bytes'
import { FileText, Film, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { UploadProgressBar } from './UploadProgressBar'
import type { PutProgress } from './put-with-progress'
import type { MultiUploadStatus } from './use-multi-image-upload'

export interface PendingUploadItem {
  localId: string
  filename: string
  contentType: string
  sizeBytes: number
  status: MultiUploadStatus
  /** Object URL for an image preview; revoked by the caller on removal. */
  previewUrl?: string
  /**
   * Bytes sent so far for this file. Absent until the transfer reports
   * its first event — the bar draws that as indeterminate.
   */
  progress?: PutProgress
}

interface PendingAttachmentStripProps {
  items: PendingUploadItem[]
  /** Cancels an in-flight upload or removes a settled one. */
  onRemove: (localId: string) => void
  /** Localized label for the `<ul>` (screen-reader group name). */
  ariaLabel: string
  /** Extra classes for the `<ul>` — surfaces tune spacing/borders. */
  className?: string
}

type FileKind = 'image' | 'video' | 'file'

function kindOf(contentType: string): FileKind {
  if (contentType.startsWith('image/')) return 'image'
  if (contentType.startsWith('video/')) return 'video'
  return 'file'
}

export function PendingAttachmentStrip({
  items,
  onRemove,
  ariaLabel,
  className,
}: PendingAttachmentStripProps) {
  const { t, i18n } = useTranslation('uploads')

  if (items.length === 0) return null

  return (
    <ul className={className ?? 'flex flex-wrap gap-2'} aria-label={ariaLabel}>
      {items.map((row) => {
        const kind = kindOf(row.contentType || 'application/octet-stream')
        const inFlight = row.status === 'pending' || row.status === 'uploading'
        const errored = row.status === 'error'
        return (
          <li
            key={row.localId}
            aria-busy={inFlight ? 'true' : undefined}
            className={
              errored
                ? 'relative flex min-w-[10rem] max-w-[14rem] flex-1 items-center gap-2 rounded-md border border-danger/40 bg-bg-elevated p-1.5'
                : 'relative flex min-w-[10rem] max-w-[14rem] flex-1 items-center gap-2 rounded-md border border-border bg-bg-elevated p-1.5'
            }
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded bg-bg-muted text-fg-muted">
              {kind === 'image' && row.previewUrl ? (
                <img src={row.previewUrl} alt="" className="h-full w-full object-cover" />
              ) : kind === 'video' ? (
                <Film aria-hidden="true" size={20} strokeWidth={1.75} />
              ) : (
                <FileText aria-hidden="true" size={20} strokeWidth={1.75} />
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-xs font-medium text-fg" title={row.filename}>
                {row.filename}
              </span>
              <span className="text-[11px] text-fg-muted">
                {inFlight
                  ? t('uploading', { count: 1 })
                  : row.status === 'done'
                    ? formatBytes(row.sizeBytes, i18n.language)
                    : errored
                      ? t('uploadFailed', { filename: row.filename })
                      : ''}
              </span>
              {inFlight && (
                <UploadProgressBar
                  progress={row.progress ?? null}
                  label={t('progress.ariaLabel', { filename: row.filename })}
                  className="mt-1"
                />
              )}
            </div>
            <button
              type="button"
              onClick={() => onRemove(row.localId)}
              aria-label={
                inFlight
                  ? t('cancelUploadAria', { filename: row.filename })
                  : t('removeAria', { filename: row.filename })
              }
              title={
                inFlight
                  ? t('cancelUploadAria', { filename: row.filename })
                  : t('removeAria', { filename: row.filename })
              }
              className="absolute -top-1.5 -end-1.5 inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border border-border bg-bg-elevated text-fg-muted shadow-sm hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X aria-hidden="true" size={12} strokeWidth={2} />
            </button>
          </li>
        )
      })}
    </ul>
  )
}
